import { useMemo } from 'react'
import { KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import type { RmaSummary, Stage } from '../api/types'
import { BOARD_STAGES, STAGE_SHORT_LABELS, STAGE_VARIANT, transitionAllowed } from '../utils/stage-meta'

interface RmaKanbanItem extends KanbanItem {
  rma: RmaSummary
}

export interface RmaKanbanProps {
  items: RmaSummary[]
  selectedId?: string
  onSelect: (rma: RmaSummary) => void
  onTransition: (rma: RmaSummary, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  return v === 'info' ? 'neutral' : v
}

const STAGES: KanbanStage[] = BOARD_STAGES.map((s) => ({
  id: s,
  label: STAGE_SHORT_LABELS[s],
  accent: kanbanAccent(STAGE_VARIANT[s]),
}))

export function RmaKanban({ items, selectedId, onSelect, onTransition }: RmaKanbanProps) {
  const kanbanItems: RmaKanbanItem[] = useMemo(
    () => items.map((rma) => ({ id: rma.id, stageId: rma.status, rma })),
    [items],
  )

  return (
    <KanbanBoard<RmaKanbanItem>
      stages={STAGES}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) => transitionAllowed(from as Stage, to as Stage)}
      onAdvance={(item, _from, to) => onTransition(item.rma, to as Stage)}
      onSelect={(item) => onSelect(item.rma)}
      renderCard={(item) => <RmaKanbanCard rma={item.rma} />}
      scrollRestorationKey="returns-rma"
    />
  )
}

function RmaKanbanCard({ rma }: { rma: RmaSummary }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-role-data-primary truncate" title={rma.customer_name ?? ''}>
        {rma.customer_name ?? <em className="text-tertiary">unknown customer</em>}
      </div>
      <div className="text-role-data-secondary truncate">
        {rma.rma_number ?? 'Not yet issued'}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-tertiary">
          {rma.line_count} line{rma.line_count === 1 ? '' : 's'}
        </span>
        {rma.has_pdf ? <span className="text-tertiary">· PDF ready</span> : null}
      </div>
      {rma.notes ? (
        <div className="line-clamp-2 text-role-metadata italic" title={rma.notes}>
          {rma.notes}
        </div>
      ) : null}
    </div>
  )
}
